import { FC, useContext, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { useMyCarDueDateAttachmentApi } from "../../http/dueDateAttachments/api";
import { DueDateAttachment } from "../../http/dueDateAttachments/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import DownloadLink from "../ui/DownloadLink";
import FileIcon from "../ui/FileIcon";
import { getDownloadAction } from "../ui/ListItemMenu";
import Table from "../ui/Table";

const DUE_DATE_ATTACHMENTS_FIELDS = ["id", "name", "dueDateId"] as const;
type DueDateAttachmentsField = (typeof DUE_DATE_ATTACHMENTS_FIELDS)[number];
type DueDateAttachmentSearch = NestedPick<DueDateAttachment, DueDateAttachmentsField>;

type DueDateAttachmentsProps = {
  dueDateId: string;
  isTeam: boolean;
};

const MyCarDueDateAttachments: FC<DueDateAttachmentsProps> = ({ dueDateId, isTeam }) => {
  const i18n = useI18n();
  const dueDateAttachmentsApi = useMyCarDueDateAttachmentApi();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const api = useApi();

  const ATTACHMENTS_FIELDS = ["id"] as const;

  const query: PaginationQueryWithFields<DueDateAttachmentSearch> = {
    ...filters.state,
    fields: ATTACHMENTS_FIELDS,
  };

  const result = dueDateAttachmentsApi.useDueDateAttachments(query, aborter.current.signal, dueDateId, isTeam);
  const [data] = useDataInitializer(result.data, result.error);

  const getAttachmentUrl = (item: DueDateAttachment) =>
    `/api/${isTeam ? "my_team_cars" : "my_cars"}/due_date_attachments/${item.id}`;

  const downloadAttachment = (item: DueDateAttachment) =>
    api.downloadFile(getAttachmentUrl(item), item.name).catch((err) => dispatch(showErrorNotification(i18n, err)));

  return (
    <>
      <Table<DueDateAttachment>
        filters={filters}
        head={
          <>
            <th align="left" style={{ width: 1 }}></th>
            <th align="left">{i18n.t.common.name}</th>
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            <Table.IconTd>
              <FileIcon fileName={item.name} />
            </Table.IconTd>
            <td>
              <DownloadLink url={getAttachmentUrl(item)} fileName={item.name}>
                {item.name}
              </DownloadLink>
            </td>
          </>
        )}
        rowActions={(item) => [getDownloadAction(i18n, () => downloadAttachment(item))]}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />
    </>
  );
};

export default MyCarDueDateAttachments;
